<template>
  <div>
    <Header :self="self" />
    <div class="container container-boards">
      <div class="alert alert-danger" role="alert" v-if="error">
        {{error.message}}
      </div>
      <div class="text-center p-3">
        <h4>{{board.name}}</h4>
        <div class="text-muted">
          <span class="m-1">{{board.itemCount}} <span v-if="board.itemCount === 1">Quote</span><span v-if="board.itemCount !== 1">Quotes</span></span>
          <span class="m-1" v-if="board.userCount > 1">-</span>
          <span class="m-1 faux-link" v-if="board.userCount > 1" v-b-modal.modal-board-users title="This is a group board">{{board.userCount}} Members</span>
          <span class="m-1" v-if="board.userCount == 1">
            <router-link :to="{ name: 'username', params: { username: board.user.username } }">@{{board.user.username}}</router-link>
          </span>
          <span class="m-1" v-if="board.private" title="This board is private">&nbsp;<i class="fas fa-lock"></i></span>
          <span class="m-1" v-if="board.hidden && !board.private" title="This board is hidden">&nbsp;<i class="fas fa-eye-slash"></i></span>
          <span class="m-1 pointer" v-b-modal.modal-board-edit v-if="board.isBoardUser"><button class="btn btn-secondary btn-sm"><i class="fas fa-edit"></i> Edit</button></span>
          <span class="m-1 pointer" v-b-modal.modal-send-board-invitation v-if="board.isBoardUser"><button class="btn btn-secondary btn-sm"><i class="fas fa-user-friends"></i> Invite</button></span>
        </div>
      </div>
        <p class="text-center gqp-header" v-if="!self.isPro">Like Quozio? You'll ❤️ Quozio Pro! <router-link :to="{name: 'get-quozio-pro', query: {ref: 'VB'}}">Check it out</router-link>.</p>
      <div class="text-center mt-4" v-if="!board.itemCount">
        <div><em>There are no quotes on this board</em></div>
        <nuxt-link to="/" class="btn btn-lg btn-primary mt-4">Create a Quote</nuxt-link>
      </div>
      <masonry class="board" :cols="{default: 3, 1199: 2, 767: 1}">
        <div v-for="(boardItem, index) in board.boardItems.data" class="text-center">
          <nuxt-link :to="{ name: 'item-boardId-itemId-itemSlug', params: { boardId: board.boardId, itemId: boardItem.boardItemId, itemSlug: boardItem.slug } }">
            <img :src="boardItem.imageUrls.medium" />
          </nuxt-link>
          <div class="user" v-if="board.userCount > 1">
            <nuxt-link :to="{ name: 'username', params: { username: boardItem.user.username } }">
              @{{boardItem.user.username}}
            </nuxt-link>
          </div>
        </div>
      </masonry>
    </div>
    <b-modal id="modal-board-edit" scrollable>
      <template v-slot:modal-title>
        Edit Your Board
      </template>
      <div class="form-group" v-bind:class="{ 'form-group-error': $v.updatedBoard.name.$error }" v-if="board.userId && board.userId == self.userId">
        <label for="name">Name</label>
        <input type="text" v-model.trim.lazy="$v.updatedBoard.name.$model" class="form-control" id="name" placeholder="What do you want to call this board?">
        <div class="error-input">Please provide a name</div>
      </div>
      <div class="form-check" v-if="board.userId && board.userId == self.userId">
        <input type="checkbox" class="form-check-input" id="private" v-model="updatedBoard.private">
        <label class="form-check-label" for="private">Make this a private board</label>
        <small class="form-text text-muted">Private boards can only be seen and accessed by you and others you invite to the board.</small>
      </div>
      <div class="form-check" v-show="!updatedBoard.private">
        <input type="checkbox" class="form-check-input" id="hidden" v-model="updatedBoard.hidden">
        <label class="form-check-label" for="hidden">Don't show this board with my profile</label>
        <small class="form-text text-muted">You will still be able to see and access this board, but others will not see it on your profile page.</small>
      </div>
      <template v-slot:modal-footer="{ ok, cancel, hide }">
        <div class="flex-grow-1" v-if="board.userId && board.userId == self.userId">
          <button class="btn btn-sm btn-danger" v-b-modal.modal-confirm-delete><i class="fas fa-trash-alt"></i> Delete</button>
        </div>
        <div class="flex-grow-1" v-if="board.userId && board.userId != self.userId">
          <button class="btn btn-sm btn-danger" v-b-modal.modal-confirm-leave><i class="fas fa-sign-out-alt"></i> Leave</button>
        </div>
        <div>
          <button class="btn btn-light btn-sm" @click="cancel()">Cancel</button>
        </div>
        <div>
          <button class="btn btn-success btn-sm ml-2" @click="updateBoard()" :disabled="submitting.updateBoard"><i class="fas fa-check"></i> Save</button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-confirm-delete" title="Delete Board" @ok="deleteBoard()">
      <p class="text-center"><i class="fas fa-exclamation-triangle fa-5x text-warning"></i></p>
      <p class="my-4">Are you sure you want to delete this board? If you proceed, this board and all the quotes on it <strong>will be deleted forever</strong>.</p>
      <p class="my-4"><em>There is no way to undo this!</em></p>
      <template v-slot:modal-footer="{ ok, cancel, hide }">
        <div class="flex-grow-1">
          <button class="btn btn-sm btn-danger" @click="deleteBoard()" :disabled="submitting.deleteBoard"><i class="fas fa-trash-alt"></i> Yes, Delete</button>
        </div>
        <div>
          <button class="btn btn-primary btn-sm" @click="cancel()">Cancel</button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-board-users" title="Board Users" ok-only>
      <div class="text-center mb-2">
        <button class="btn btn-primary btn-success btn-pill" v-b-modal.modal-send-board-invitation>Invite New User</button>
      </div>
      <div v-for="(boardUser, index) in boardUsers.data">
        {{boardUser.name}} <nuxt-link :to="{ name: 'username', params: { username: boardUser.username } }">
          @{{boardUser.username}}
        </nuxt-link>
      </div>
      <div class="mt-3" v-if="boardUsers.pageNext && boardUsers.data.length < board.userCount">
        <button class="btn btn-secondary btn-sm" @click="loadBoardUsers(boardUsers.pageNext)">Load More</button>
      </div>
    </b-modal>

    <b-modal id="modal-confirm-leave" title="Leave Board" @ok="leaveBoard()">
      <p class="text-center"><i class="fas fa-exclamation-triangle fa-5x text-warning"></i></p>
      <p class="my-4">Are you sure you want to leave this board? If you proceed, you will need to be re-invited to join the board.</p>
      <template v-slot:modal-footer="{ ok, cancel, hide }">
        <div class="flex-grow-1">
          <button class="btn btn-sm btn-danger" @click="leaveBoard()" :disabled="submitting.leaveBoard"><i class="fas fa-trash-alt"></i> Yes, Leave</button>
        </div>
        <div>
          <button class="btn btn-primary btn-sm" @click="cancel()">Cancel</button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-send-board-invitation" title="Invite Others">
      <div class="alert alert-danger" role="alert" v-if="modalError">
        {{modalError.message}}
      </div>
      <p class="text-center"><i class="fas fa-user-friends fa-5x text-primary"></i></p>
      <p class="my-4">When you invite others to this board, they will be able to create and save quotes to it just like you. It's a fun way to share and discover meaningful words and quotes.</p>
      <p class="my-4">Enter the email address of the person you would like to invite and we'll send them a message on how to join.</p>
      <p class="my-4">
        <div class="form-group">
          <label for="email" class="text-center"><strong>Email Address</strong></label>
          <input type="email" id="email" v-model="boardInvitation.email" class="form-control" placeholder="someone@example.com" />
        </div>
      </p>
      <template v-slot:modal-footer="{ ok, cancel, hide }">
        <div>
          <button class="btn btn-primary btn-sm" @click="sendInvitation()" :disabled="submitting.sendInvitation">Send</button>
        </div>
      </template>
    </b-modal>

    <Footer />
  </div>
</template>

<script>
  import Header from "@/components/header";
  import Footer from "@/components/footer";
  import { mapState } from "vuex";
  import { required } from "vuelidate/lib/validators";
  const boardItemLimit = 25; // You need enough items that a scrollbar will be generated for the page so that new items will trigger when they scroll to the bottom.
  let isScrolling;

  export default {
    name: "App",
    components: { Header, Footer },
    data() {
      return {
        board: { boardItems: { data: [] }, user: {} },
        boardInvitation: { email: null },
        boardUsers: { data: [] },
        loadingBoardItems: false,
        updatedBoard: { name: null, private: false, hidden: false },
        error: null,
        modalError: null,
        submitting: { updateBoard: false, deleteBoard: false, leaveBoard: false, sendInvitation: false },
      }
    },
    head({ $config }) {
      let previewImage = this.board.previewImageUrls ? this.board.previewImageUrls.large : "";
      return {
        title: `${this.board.name} - Quozio`,
        link: [
          { rel: 'canonical', href: `${$config.BASE_URL}/board/${this.board.boardId}/${this.board.slug}` }
        ],
        meta: [
          { hid: "og:url", name: "og:url", content: `${$config.BASE_URL}/board/${this.board.boardId}/${this.board.slug}` },
          { hid: "og:title", name: "og:title", content: this.board.name },
          { hid: "og:image", name: "og:image", content: previewImage }
        ]
      }
    },
    async asyncData({ store, app, params, error, redirect, $config }) {
      await store.dispatch("self/get", app.$cookies);
      let response = await app.$dataService.get(`/boards/${params.boardId}`, { expand: "user", includePreviewImages: true });
      if (response.error) {
        return error({ statusCode: response.error.status, message: response.error.message, reference: response.error.reference });
      }

      // Make sure we keep the slug consistent
      if (response.data.slug != params.boardSlug) {
        return redirect(301, `${$config.BASE_URL}/board/${response.data.boardId}/${response.data.slug}`);
      }

      return { board: response.data };
    },
    async mounted() {
      let response = await this.$dataService.get(`/boards/${this.$route.params.boardId}/boardItems`, { sortBy: "dateCreated", desc: true, limit: boardItemLimit, expand: "user" });
      if (response.error) {
        this.error = response.error;
        return;
      }
      let boardItems = response.data;

      Object.assign(this.updatedBoard, this.board);
      this.board.boardItems = boardItems;

      window.addEventListener("scroll", this.checkLoadBoardItems, false);

      this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
        if (modalId == "modal-board-users") {
          // Don't load if you've already loaded the users.
          if (!this.boardUsers.data.length) {
            this.loadBoardUsers();
          }
        }
      })

    },
    methods: {
      async getMoreBoardItems() {
        if (this.board && this.board.boardItems.pageNext) {
          this.loadingBoardItems = true;
          let response = await this.$dataService.get(this.board.boardItems.pageNext);
          if (response.error) {
            this.error = response.error;
            return;
          }
          let newItems = response.data;

          this.board.boardItems.data = this.board.boardItems.data.concat(newItems.data);
          this.board.boardItems.pageNext = newItems.pageNext;
          this.loadingBoardItems = false;
        }
      },
      checkLoadBoardItems() {
        window.clearTimeout(isScrolling);
        let vm = this;
        isScrolling = setTimeout(function () {
          var el = document.getElementsByTagName("HTML")[0];
          if (!vm.loadingBoardItems && el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            vm.getMoreBoardItems();
          }
        }, 75);
      },
      async updateBoard() {

        this.submitting.updateBoard = true;
        this.$nuxt.$loading.start();
        if (this.updatedBoard.private) {
          this.updatedBoard.hidden = true;
        }
        this.$bvModal.hide("modal-board-edit");
        let response;

        if (this.board.userId == this.self.userId) {
          response = await this.$dataService.put(`/boards/${this.board.boardId}`, this.updatedBoard);
        } else {
          if (this.updatedBoard.hidden) {
            response = await this.$dataService.post(`/boards/${this.board.boardId}/hide`);
          } else {
            response = await this.$dataService.post(`/boards/${this.board.boardId}/unhide`);
          }
        }

        this.submitting.updateBoard = false;
        this.$nuxt.$loading.finish();

        if (response.error) {
          this.error = response.error;

          return;
        }

        this.board.hidden = this.updatedBoard.hidden;
        if (this.board.userId == this.self.userId) {
          this.board.name = this.updatedBoard.name, this.board.private = this.updatedBoard.private;
        }

      },
      async deleteBoard() {

        this.submitting.deleteBoard = true;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.delete(`/boards/${this.board.boardId}`);
        this.submitting.deleteBoard = false;
        this.$nuxt.$loading.finish();

        if (response.error) {
          this.error = response.error;
          return;
        }

        this.$router.push({ name: "username", params: { username: this.self.username } });
      },
      async leaveBoard() {

        this.submitting.leaveBoard = true;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.delete(`/boards/${this.board.boardId}/boardUsers/${this.self.userId}`);
        this.submitting.leaveBoard = false;
        this.$nuxt.$loading.finish();

        if (response.error) {
          this.error = response.error;
          return;
        }

        this.$nuxt.$loading.finish();

        this.$toasted.show("You have left the board", {
          theme: "outline",
          position: "bottom-center",
          duration: 5000,
          iconPack: "fontawesome",
          icon: "thumbs-up",
          type: "success",
          keepOnHover: true,
        });

        this.$router.push({ name: "username", params: { username: this.self.username } });
      },
      async sendInvitation() {

        let payload = { email: this.boardInvitation.email };

        this.submitting.sendInvitation = true;
        this.$nuxt.$loading.start();
        this.modalError = null;
        let response = await this.$dataService.post(`/boards/${this.$route.params.boardId}/boardInvitations`, payload);
        this.submitting.sendInvitation = false;
        if (response.error) {
          this.modalError = response.error;
          this.$nuxt.$loading.finish();
          return;
        }
        this.$bvModal.hide("modal-send-board-invitation");
        this.boardInvitation.email = null;

        this.$nuxt.$loading.finish();

        this.$ga.event("Board", "Board Invitation Sent", this.$route.params.boardId);

        let toast = this.$toasted.show("Sent!", {
          theme: "outline",
          position: "bottom-center",
          duration: 5000,
          iconPack: "fontawesome",
          icon: "thumbs-up",
          type: "success",
          keepOnHover: true,
        });
      },
      async loadBoardUsers(url) {
        url = url || `/boards/${this.$route.params.boardId}/boardUsers`;
        let response = await this.$dataService.get(url);
        if (response.error) {
          return;
        }

        this.boardUsers.data = this.boardUsers.data.concat(response.data.data);
        this.boardUsers.pageNext = response.data.pageNext;

      }
    },
    beforeDestroy: function () {
      window.removeEventListener("scroll", this.checkLoadBoardItems, false);
    },
    computed: {
      ...mapState("self", { self: "self" }),
    },
    validations: {
      updatedBoard: {
        name: {
          required
        }
      }
    }
  };
</script>
